import { useWebsiteConfiguration } from '@/components/contexts/WebsiteConfigurationProvider'
import SyllabusPicker from '@/components/custom/SyllabusPicker'
import { GridCol } from '@/components/nsw/grid/GridCol'
import { GridWrapper } from '@/components/nsw/grid/GridWrapper'
import { SYLLABUS_CONTENT_MODEL_CEC } from '@/constants'
import { TREE_ID_LABELS } from '@/constants/treepickeroptions'
import { Syllabus } from '@/kontent/content-types'
import { TaxoKeyLearningArea } from '@/kontent/taxonomies'
import TreePicker from '@/legacy-ported/components/custom/TreePicker'
import { TreeElement } from '@/legacy-ported/components/custom/treeUtils'
import { arrayToggleMultiple } from '@/legacy-ported/utilities/functions'
import { PrintSyllabus } from '@/pages/page-api/syllabus/[syllabus]'
import { TaxoStageWithLifeSkill } from '@/types'
import {
	byTaxoCodename,
	isAllowPreviewExternalSyllabus,
	isIntersect,
	isYes,
} from '@/utils'
import { getSyllabusTitleWithPublicationYear } from '@/utils/syllabus/getSyllabusTitleWithPublicationYear'
import { ElementModels } from '@kontent-ai/delivery-sdk'
import {
	forwardRef,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react'
export const makeLearningAreaTree = (
	syllabuses: Syllabus[],
	keyLearningAreas: ElementModels.TaxonomyTerm<TaxoKeyLearningArea>[],
	isSyllabusValueFromSyllabusTaxoCodename = false,
	disabledKeyLearningAreas = [] as TaxoKeyLearningArea[],
	withPublicationYear = true,
): TreeElement[] =>
	keyLearningAreas.map((kla) => {
		const children = syllabuses
			.filter(
				(syl) =>
					syl.elements.key_learning_area__items.value.some(
						(sylKla) => sylKla.codename === kla.codename,
					) &&
					(isAllowPreviewExternalSyllabus() ||
						!isYes(syl.elements.doredirect)),
			)
			.map((syl) => {
				const syllabusTitleWithPublicationYear =
					getSyllabusTitleWithPublicationYear(syl, false)

				// Identify senior mainstream syllabuses and their related life skills
				const isStage6 =
					syl.elements.stages__stages?.value?.some(
						(s) => s.codename === 'stage_6',
					) || false
				const isLifeSkills =
					syl.elements.syllabus_type__items?.value?.some(
						(type) => type.codename === 'life_skills',
					) || false
				const isSeniorMainstream = isStage6 && !isLifeSkills
				return {
					id: isSyllabusValueFromSyllabusTaxoCodename
						? syl.elements.syllabus.value?.[0]?.codename
						: syl.system.codename,
					label: withPublicationYear
						? syllabusTitleWithPublicationYear
						: syl.elements.title.value,
					disabled: isIntersect(
						syl.elements.key_learning_area__items.value.map(
							byTaxoCodename,
						),
						disabledKeyLearningAreas,
					),
					moreInfo:
						syl.elements.syllabus_print_tooltip?.value.replace(
							'<p><br></p>',
							'',
						) || undefined,
					hasRelatedLifeSkills:
						!!syl.elements.relatedlifeskillssyllabus?.value?.length,
					isSeniorMainstream,
					relatedLifeSkillsInfo:
						syl.elements.relatedlifeskillssyllabus?.value?.map(
							(lsCodename: string) => {
								// Find the actual syllabus object using the codename
								const relatedSyllabus = syllabuses.find(
									(s) => s.system.codename === lsCodename,
								)
								return {
									codename: lsCodename,
									title:
										relatedSyllabus?.elements?.title
											?.value || lsCodename,
									publicationYear:
										relatedSyllabus?.elements
											?.publication_year?.value || '',
								}
							},
						) || [],
				}
			})
		const childrenNotDisabled = children.filter((c) => !c.disabled)

		return {
			id: kla.codename,
			label: kla.name,
			disabled: !childrenNotDisabled.length,
			children: children,
		}
	})

// Adapted version for PrintSyllabus data from API
export const makeLearningAreaTreeFromPrintSyllabus = (
	syllabuses: PrintSyllabus[],
	keyLearningAreas: ElementModels.TaxonomyTerm<TaxoKeyLearningArea>[],
	disabledKeyLearningAreas = [] as TaxoKeyLearningArea[],
	withPublicationYear = true,
): TreeElement[] =>
	keyLearningAreas.map((kla) => {
		const children = syllabuses
			.filter((syl) => syl.kla.includes(kla.codename))
			.map((syl) => {
				const syllabusTitle = withPublicationYear
					? `${syl.title} (${syl.publicationYear})`
					: syl.title

				const disabled = disabledKeyLearningAreas.some(
					(d) => d === kla.codename,
				)

				return {
					id: syl.cmsCodename,
					label: syllabusTitle,
					disabled,
				}
			})
		const childrenNotDisabled = children.filter((c) => !c.disabled)

		return {
			id: kla.codename,
			label: kla.name,
			disabled: !childrenNotDisabled.length,
			children: children,
		}
	})

export const makeSyllabusesFromPrintSyllabus = (
	syllabuses: PrintSyllabus[],
	disabledKeyLearningAreas: TaxoKeyLearningArea[] = [],
	_withPublicationYear = true,
): TreeElement[] => {
	return syllabuses
		.filter((syl) => {
			// Only show syllabuses that are not disabled
			return !disabledKeyLearningAreas.some((disabled) =>
				syl.kla.includes(disabled),
			)
		})
		.map((syl) => {
			// syl.title already contains the publication year, so use it directly
			return {
				id: syl.cmsCodename,
				label: syl.title,
				disabled: isIntersect(syl.kla, disabledKeyLearningAreas),
				moreInfo:
					syl.syllabus_print_tooltip?.replace('<p><br></p>', '') ||
					undefined,
				hasRelatedLifeSkills:
					!!syl.relatedLifeSkillsSyllabusCmsCodename,
				relatedLifeSkillsInfo: syl.relatedLifeSkillsSyllabusCmsCodename
					? [
							{
								codename:
									syl.relatedLifeSkillsSyllabusCmsCodename,
								title:
									syllabuses.find(
										(s) =>
											s.cmsCodename ===
											syl.relatedLifeSkillsSyllabusCmsCodename,
									)?.title ||
									syl.relatedLifeSkillsSyllabusCmsCodename,
								publicationYear:
									syllabuses.find(
										(s) =>
											s.cmsCodename ===
											syl.relatedLifeSkillsSyllabusCmsCodename,
									)?.publicationYear || '',
							},
					  ]
					: [],
				seoKeywords: syl.seoKeywords || '',
				isCECSyllabus:
					syl.syllabusContentModel === SYLLABUS_CONTENT_MODEL_CEC,
			}
		})
}

export const makeSyllabuses = (
	syllabuses: Syllabus[],
	disabledKeyLearningAreas: TaxoKeyLearningArea[] = [],
	withPublicationYear = true,
): TreeElement[] => {
	return syllabuses
		.filter(
			(syl) =>
				isAllowPreviewExternalSyllabus() ||
				!isYes(syl.elements.doredirect),
		)
		.map((syl) => {
			const syllabusTitleWithPublicationYear =
				getSyllabusTitleWithPublicationYear(syl, false)

			// Identify senior mainstream syllabuses and their related life skills

			return {
				id: syl.system.codename,
				label: withPublicationYear
					? syllabusTitleWithPublicationYear
					: syl.elements.title.value,
				disabled: isIntersect(
					syl.elements.key_learning_area__items.value.map(
						byTaxoCodename,
					),
					disabledKeyLearningAreas,
				),
				moreInfo:
					syl.elements.syllabus_print_tooltip?.value.replace(
						'<p><br></p>',
						'',
					) || undefined,
				hasRelatedLifeSkills:
					!!syl.elements.relatedlifeskillssyllabus?.value?.length,
				relatedLifeSkillsInfo:
					syl.elements.relatedlifeskillssyllabus?.value?.map(
						(lsCodename: string) => {
							// Find the actual syllabus object using the codename
							const relatedSyllabus = syllabuses.find(
								(s) => s.system.codename === lsCodename,
							)
							return {
								codename: lsCodename,
								title:
									relatedSyllabus?.elements?.title?.value ||
									lsCodename,
								publicationYear:
									relatedSyllabus?.elements?.publication_year
										?.value || '',
							}
						},
					) || [],
				hasExamplesIn: !!syl.elements.has_examples_in?.value?.length,
				stages: syl.elements.stages__stages?.value || [],
			}
		})
}

export const makeStageOptions = (
	stages: ElementModels.TaxonomyTerm<TaxoStageWithLifeSkill>[],
	disabledStages: TaxoStageWithLifeSkill[] = [],
): TreeElement[] => {
	const _temp = stages.map((s) => ({
		id: s.codename,
		label: s.name,
		disabled: disabledStages.includes(s.codename),
	}))
	return _temp
}

export const makeNestedStageGroups = (
	stages: ElementModels.TaxonomyTerm<TaxoStageWithLifeSkill>[],
	disabledStages: TaxoStageWithLifeSkill[] = [],
	customLifeSkillsLabel?: string,
	_hideLifeSkillsDivider = false,
	selectedSyllabuses: string[] = [],
	allSyllabuses: PrintSyllabus[] = [],
): TreeElement[] => {
	const selectedCECSyllabus = allSyllabuses.filter(
		(syl) =>
			selectedSyllabuses.includes(syl.cmsCodename) &&
			syl.syllabusContentModel === SYLLABUS_CONTENT_MODEL_CEC,
	)

	// Disable all stages if no syllabuses are selected
	const noSyllabusesSelected =
		!selectedSyllabuses ||
		selectedSyllabuses.length === 0 ||
		selectedCECSyllabus.length === selectedSyllabuses.length

	// Collect all stages that are available in the selected syllabuses
	const availableStagesFromSyllabuses = new Set<string>()
	if (!noSyllabusesSelected) {
		selectedSyllabuses.forEach((selectedCodename) => {
			const syllabusData = allSyllabuses.find(
				(syl) => syl.cmsCodename === selectedCodename,
			)
			if (
				syllabusData?.stages &&
				syllabusData.syllabusContentModel !== SYLLABUS_CONTENT_MODEL_CEC
			) {
				syllabusData.stages.forEach((stage) => {
					availableStagesFromSyllabuses.add(stage)
				})
			}
		})
	}

	const primaryStages = stages
		.filter((stage) =>
			['early_stage_1', 'stage_1', 'stage_2', 'stage_3'].includes(
				stage.codename,
			),
		)
		.map((s) => ({
			id: s.codename,
			label: s.name,
			disabled:
				noSyllabusesSelected ||
				disabledStages.includes(s.codename) ||
				(!noSyllabusesSelected &&
					!availableStagesFromSyllabuses.has(s.codename)),
		}))

	// Secondary stages (Stages 4-5 correspond to years 7-10)
	const secondaryStagesBase = stages
		.filter((stage) => ['stage_4', 'stage_5'].includes(stage.codename))
		.map((s) => ({
			id: s.codename,
			label: s.name,
			disabled:
				noSyllabusesSelected ||
				disabledStages.includes(s.codename) ||
				(!noSyllabusesSelected &&
					!availableStagesFromSyllabuses.has(s.codename)),
		}))

	// Add Life Skills to secondary group with custom label and divider control
	const lifeSkillsStage = stages.find(
		(stage) => stage.codename === 'life_skills',
	)
	const secondaryStages = [...secondaryStagesBase]
	if (lifeSkillsStage) {
		// Life skills can be enabled if it's in selected syllabuses OR if any of stage_4, stage_5, stage_6 are available
		const canEnableLifeSkills =
			availableStagesFromSyllabuses.has(lifeSkillsStage.codename) ||
			availableStagesFromSyllabuses.has('stage_4') ||
			availableStagesFromSyllabuses.has('stage_5')

		secondaryStages.push({
			id: lifeSkillsStage.codename,
			label: customLifeSkillsLabel || lifeSkillsStage.name,
			disabled:
				noSyllabusesSelected ||
				disabledStages.includes(lifeSkillsStage.codename) ||
				(!noSyllabusesSelected && !canEnableLifeSkills),
		})
	}

	// Senior (11-12) - hoist as direct option instead of nested group
	const stage6 = stages.find((stage) => stage.codename === 'stage_6')

	const stageGroups: TreeElement[] = []

	if (primaryStages.length > 0) {
		const allPrimaryDisabled =
			noSyllabusesSelected ||
			primaryStages.every((stage) => stage.disabled)
		stageGroups.push({
			id: 'primary-group',
			label: 'Primary (K–6)',
			children: primaryStages,
			disabled: allPrimaryDisabled,
		})
	}

	if (secondaryStages.length > 0) {
		const allSecondaryDisabled =
			noSyllabusesSelected ||
			secondaryStages.every((stage) => stage.disabled)
		stageGroups.push({
			id: 'secondary-group',
			label: 'Secondary (7–10)',
			children: secondaryStages,
			disabled: allSecondaryDisabled,
		})
	}

	// Hoist Senior (11-12) as direct option
	if (stage6) {
		stageGroups.push({
			id: stage6.codename,
			label: 'Senior (11–12)',
			disabled:
				noSyllabusesSelected ||
				disabledStages.includes(stage6.codename) ||
				(!noSyllabusesSelected &&
					!availableStagesFromSyllabuses.has(stage6.codename)),
		})
	}

	return stageGroups
}

export interface CustomSyllabusPickerProps {
	syllabuses: PrintSyllabus[]
	keyLearningAreas: ElementModels.TaxonomyTerm<TaxoKeyLearningArea>[]
	stages: ElementModels.TaxonomyTerm<TaxoStageWithLifeSkill>[]
	showLearningAreaError: boolean
	showSyllabusError: boolean
	showStageError: boolean
	showElementsError: boolean
	disabledLearningAreas: TaxoKeyLearningArea[]
	disabledStages: TaxoStageWithLifeSkill[]
	onChange: (_selected: {
		learningAreas: string[]
		selectedSyllabuses: string[]
		stages: string[]
		elements: string[]
	}) => void
	syllabusElementsOptions: TreeElement[]

	initialSelectedSyllabuses?: string[]
	initialSelectedStages?: string[]
	initialSelectedElements?: string[]

	// Props for makeNestedStageGroups feature (always enabled)
	customLifeSkillsLabel?: string
	hideLifeSkillsDivider?: boolean

	// Props for createElementsWithSelectAll feature
	showElementsSelectAll?: boolean // defaults to true to match legacy behavior

	// Props for auto-selection from query string
	autoSelectSyllabusCode?: string

	maxSyllabusesFromConfig?: number
	maxSyllabusesMessageFromConfig?: string
}

const NewCustomSyllabusPicker = forwardRef<
	HTMLDivElement,
	CustomSyllabusPickerProps
>((props, ref) => {
	const {
		onChange,
		syllabuses: allSyllabuses,
		stages: allStages,
		showLearningAreaError: _showLearningAreaError,
		showSyllabusError: _showSyllabusError,
		showStageError: _showStageError,
		showElementsError: _showElementsError,
		disabledLearningAreas,
		disabledStages,
		syllabusElementsOptions,
		initialSelectedSyllabuses = [],
		initialSelectedStages = [],
		initialSelectedElements = [],
		customLifeSkillsLabel,
		hideLifeSkillsDivider = false,
		showElementsSelectAll = true,
		autoSelectSyllabusCode,
		maxSyllabusesFromConfig,
		maxSyllabusesMessageFromConfig,
	} = props

	// Get website configuration for max syllabuses settings
	const _websiteConfiguration = useWebsiteConfiguration()
	// const maxSyllabusesFromConfig =
	// 	websiteConfiguration?.item?.elements?.max_download_syllabuses?.value ||
	// 	10
	// const maxSyllabusesMessageFromConfig = (
	// 	websiteConfiguration?.item?.elements?.max_download_syllabuses_message
	// 		?.value ||
	// 	'Maximum ${max-syllabuses-download} number of syllabuses reached.'
	// ).replace('${max-syllabuses-download}', maxSyllabusesFromConfig.toString())

	// selected items
	const [learningAreas, setLearningAreas] = useState<string[]>(
		initialSelectedSyllabuses,
	)
	const [selectedSyllabuses, setSelectedSyllabuses] = useState<string[]>(
		initialSelectedSyllabuses || [],
	)
	const [pendingSyllabuses, setPendingSyllabuses] = useState<string[]>([]) // Items waiting to move
	const [stages, setStages] = useState<string[]>(initialSelectedStages)
	const [elements, setElements] = useState<string[]>(initialSelectedElements)

	// Track if we've already performed auto-selection
	const hasAutoSelected = useRef(false)

	// Auto-select syllabus from query string parameter (only on initial load)
	useEffect(() => {
		if (
			autoSelectSyllabusCode &&
			allSyllabuses.length > 0 &&
			!hasAutoSelected.current
		) {
			// Find syllabus by matching system codename
			const matchingSyllabus = allSyllabuses.find(
				(syl) => syl.cmsCodename === autoSelectSyllabusCode,
			)

			if (
				matchingSyllabus &&
				!selectedSyllabuses.includes(matchingSyllabus.cmsCodename)
			) {
				// Auto-select this syllabus
				const updatedSyllabuses = [
					...selectedSyllabuses,
					matchingSyllabus.cmsCodename,
				]
				setSelectedSyllabuses(updatedSyllabuses)

				// Trigger onChange to update parent component
				if (onChange) {
					onChange({
						learningAreas,
						selectedSyllabuses: updatedSyllabuses,
						stages,
						elements,
					})
				}

				// Mark that we've performed auto-selection
				hasAutoSelected.current = true
			}
		}
	}, [
		autoSelectSyllabusCode,
		allSyllabuses,
		selectedSyllabuses,
		learningAreas,
		stages,
		elements,
		onChange,
	])

	const _handleLearningAreas = (ids: string[]) => {
		const updated = arrayToggleMultiple(learningAreas, ids)
		setLearningAreas(updated)

		if (onChange) {
			onChange({
				learningAreas: updated,
				selectedSyllabuses,
				stages,
				elements,
			})
		}
	}

	const handleSyllabus = (ids: string[]) => {
		if (ids.length > 0) {
			const clickedId = ids[0]

			// Check if this is a selection (not deselection)
			const isSelecting =
				!selectedSyllabuses.includes(clickedId) &&
				!pendingSyllabuses.includes(clickedId)

			if (isSelecting) {
				// Add to pending state immediately (shows as checked but stays in unselected section)
				setPendingSyllabuses((prev) => [...prev, clickedId])

				// After 0.5 seconds, move from pending to selected
				setTimeout(() => {
					setPendingSyllabuses((prev) =>
						prev.filter((id) => id !== clickedId),
					)
					setSelectedSyllabuses((prev) => {
						const updated = [...prev, clickedId]

						// Perform all the existing logic with the updated selection
						performSyllabusSelectionLogic(updated)
						return updated
					})
				}, 500)
			} else {
				// Handle deselection immediately (remove from selected or pending)
				if (pendingSyllabuses.includes(clickedId)) {
					setPendingSyllabuses((prev) =>
						prev.filter((id) => id !== clickedId),
					)
				} else {
					const updated = selectedSyllabuses.filter(
						(id) => id !== clickedId,
					)
					setSelectedSyllabuses(updated)
					performSyllabusSelectionLogic(updated)
				}
			}
		}
	}

	// Extract the complex syllabus logic into a separate function
	const performSyllabusSelectionLogic = (updatedSyllabuses: string[]) => {
		// Clear stages when no syllabuses are selected
		let updatedStages = stages
		let updatedElements = elements

		if (!updatedSyllabuses || updatedSyllabuses.length === 0) {
			updatedStages = []
			setStages(updatedStages)
			updatedElements = []
			setElements(updatedElements)
		} else {
			// Collect available stages from selected syllabuses
			const availableStagesFromSyllabuses = new Set<string>()
			updatedSyllabuses.forEach((selectedCodename) => {
				const syllabusData = allSyllabuses.find(
					(syl) => syl.cmsCodename === selectedCodename,
				)
				if (
					syllabusData?.stages &&
					syllabusData.syllabusContentModel !==
						SYLLABUS_CONTENT_MODEL_CEC
				) {
					syllabusData.stages.forEach((stage) => {
						availableStagesFromSyllabuses.add(stage)
					})
				}
			})

			// Add life_skills to available stages if stage_4, stage_5, or stage_6 are available
			if (
				availableStagesFromSyllabuses.has('stage_4') ||
				availableStagesFromSyllabuses.has('stage_5')
			) {
				availableStagesFromSyllabuses.add('life_skills')
			}

			// Filter out selected stages that are no longer available
			updatedStages = stages.filter((stageId) =>
				availableStagesFromSyllabuses.has(stageId),
			)
			if (updatedStages.length !== stages.length) {
				setStages(updatedStages)
			}

			// Filter out selected elements that are no longer available
			// Check if any selected syllabus has examples for "examples" element
			const hasExamplesInSelectedSyllabuses = updatedSyllabuses.some(
				(selectedCodename) => {
					const syllabusData = allSyllabuses.find(
						(syl) => syl.cmsCodename === selectedCodename,
					)
					return !!syllabusData?.has_examples
				},
			)

			// Check if English K-10 or Mathematics K-10 are selected for "tags" element
			const hasEnglishK10 = updatedSyllabuses.some((codename) =>
				codename.includes('english_k_10'),
			)
			const hasMathematicsK10 = updatedSyllabuses.some((codename) =>
				codename.includes('mathematics_k_10'),
			)

			// Check if Early Stage 1 is available for "access-points" element
			const hasEarlyStage1 =
				availableStagesFromSyllabuses.has('early_stage_1')
			// Filter selected elements based on what's available
			updatedElements = elements.filter((elementId) => {
				if (
					elementId === 'examples' &&
					!hasExamplesInSelectedSyllabuses
				)
					return false
				if (
					elementId === 'tags' &&
					!hasEnglishK10 &&
					!hasMathematicsK10
				)
					return false
				if (elementId === 'access-points' && !hasEarlyStage1)
					return false
				return true
			})
			if (updatedElements.length !== elements.length) {
				setElements(updatedElements)
			}
		}

		if (onChange) {
			onChange({
				learningAreas,
				selectedSyllabuses: updatedSyllabuses,
				stages: updatedStages,
				elements: updatedElements,
			})
		}
	}

	const handleStages = (ids: string[]) => {
		const updated = arrayToggleMultiple(stages, ids)
		setStages(updated)

		if (onChange) {
			onChange({
				learningAreas,
				selectedSyllabuses,
				stages: updated,
				elements,
			})
		}
	}

	const handleElements = (ids: string[]) => {
		let updated = arrayToggleMultiple(elements, ids)
		// DC-357 Clear AccessPoints and Examples when content is unselected
		if (
			ids.includes(TREE_ID_LABELS.CONTENT.id) &&
			!updated.includes(TREE_ID_LABELS.CONTENT.id)
		) {
			updated = updated.filter(
				(element) =>
					//element !== TREE_ID_LABELS.ADDRESSED_IN_PARALLEL.id &&
					element !== TREE_ID_LABELS.ACCESS_POINTS.id &&
					element !== TREE_ID_LABELS.EXAMPLES.id &&
					element !== TREE_ID_LABELS.CURRICULUM_CONNECTIONS.id &&
					element !== TREE_ID_LABELS.TAGS.id,
			)
		}
		// DC-357 Select content if AccessPoints or Examples are selected
		if (
			//updated.includes(TREE_ID_LABELS.ADDRESSED_IN_PARALLEL.id) ||
			(updated.includes(TREE_ID_LABELS.ACCESS_POINTS.id) ||
				updated.includes(TREE_ID_LABELS.EXAMPLES.id) ||
				updated.includes(TREE_ID_LABELS.CURRICULUM_CONNECTIONS.id) ||
				updated.includes(TREE_ID_LABELS.TAGS.id)) &&
			!updated.includes(TREE_ID_LABELS.CONTENT.id)
		) {
			updated.push(TREE_ID_LABELS.CONTENT.id)
		}

		setElements(updated)

		if (onChange) {
			onChange({
				learningAreas,
				selectedSyllabuses,
				stages,
				elements: updated,
			})
		}
	}

	const createElementsWithSelectAll = useCallback(
		(syllabusElementsOptions: TreeElement[]): TreeElement[] => {
			// Check if no syllabuses are selected (which disables all stages)

			const selectedCECSyllabus = allSyllabuses.filter(
				(syl) =>
					selectedSyllabuses.includes(syl.cmsCodename) &&
					syl.syllabusContentModel === SYLLABUS_CONTENT_MODEL_CEC,
			)

			const noSyllabusesSelected =
				!selectedSyllabuses ||
				selectedSyllabuses.length === 0 ||
				selectedCECSyllabus.length === selectedSyllabuses.length

			// Check if Early Stage 1 would be disabled (same logic as makeNestedStageGroups)
			const isEarlyStage1Disabled =
				noSyllabusesSelected || disabledStages.includes('early_stage_1')

			// Also check if Early Stage 1 is available from selected syllabuses
			const availableStagesFromSyllabuses = new Set<string>()
			selectedSyllabuses.forEach((syllabusCodename) => {
				const syllabusData = allSyllabuses.find(
					(syl) => syl.cmsCodename === syllabusCodename,
				)
				if (
					syllabusData?.stages &&
					syllabusData.syllabusContentModel !==
						SYLLABUS_CONTENT_MODEL_CEC
				) {
					syllabusData?.stages?.forEach((stage) => {
						availableStagesFromSyllabuses.add(stage)
					})
				}
			})
			const isEarlyStage1Available =
				availableStagesFromSyllabuses.has('early_stage_1')

			// Access content points should be disabled if Early Stage 1 is disabled OR not available
			const shouldDisableAccessPoints =
				isEarlyStage1Disabled || !isEarlyStage1Available

			// Check if neither English K-10 nor Mathematics K-10 are selected
			const hasEnglishK10 = selectedSyllabuses.some((codename) =>
				codename.startsWith('english_k_10'),
			)
			const hasMathematicsK10 = selectedSyllabuses.some((codename) =>
				codename.startsWith('mathematics_k_10'),
			)
			const shouldDisableTags = !hasEnglishK10 && !hasMathematicsK10

			// Check if none of selected syllabuses have examples
			const hasExamplesInSelectedSyllabuses = selectedSyllabuses.some(
				(selectedCodename) => {
					const syllabusData = allSyllabuses.find(
						(syl) => syl.cmsCodename === selectedCodename,
					)
					return !!syllabusData?.has_examples
				},
			)
			const shouldDisableExamples = !hasExamplesInSelectedSyllabuses

			// Create a copy of the syllabusElementsOptions with conditional disabling
			const modifiedElements = syllabusElementsOptions.map((element) => {
				// If no syllabuses are selected, disable ALL elements
				if (noSyllabusesSelected) {
					return {
						...element,
						disabled: true,
						children: element.children?.map((child) => ({
							...child,
							disabled: true,
						})),
					}
				}

				// Check if this element has children (like "all-content" > "access-points")
				if (element.children && element.children.length > 0) {
					const modifiedChildren = element.children.map((child) => {
						// If this child is "access-points" and Early Stage 1 is disabled, disable it
						if (
							child.id === 'access-points' &&
							shouldDisableAccessPoints
						) {
							return {
								...child,
								disabled: true,
							}
						}
						// If this child is "examples" and no selected syllabuses have examples, disable it
						if (child.id === 'examples' && shouldDisableExamples) {
							return {
								...child,
								disabled: true,
							}
						}
						return child
					})

					return {
						...element,
						children: modifiedChildren,
					}
				}

				// Check top-level elements for both access-points and tags
				if (
					element.id === 'access-points' &&
					shouldDisableAccessPoints
				) {
					return {
						...element,
						disabled: true,
					}
				}

				// If this element is "tags" and neither English K-10 nor Mathematics K-10 are selected, disable it
				if (element.id === 'tags' && shouldDisableTags) {
					return {
						...element,
						disabled: true,
					}
				}

				// If this element is "examples" and no selected syllabuses have examples, disable it
				if (element.id === 'examples' && shouldDisableExamples) {
					return {
						...element,
						disabled: true,
					}
				}

				return element
			})

			if (!showElementsSelectAll) {
				return modifiedElements
			}

			return [
				{
					id: 'select-all',
					label: 'Select all',
					children: modifiedElements,
					disabled: noSyllabusesSelected, // Disable "Select all" when no syllabuses selected
				},
			]
		},
		[
			selectedSyllabuses,
			disabledStages,
			showElementsSelectAll,
			allSyllabuses,
		],
	)

	// Memoize the elements to ensure they update when selectedSyllabuses or disabledStages change
	const processedSyllabusElements = useMemo(() => {
		return createElementsWithSelectAll(syllabusElementsOptions)
	}, [syllabusElementsOptions, createElementsWithSelectAll])

	return (
		<div ref={ref}>
			<GridWrapper>
				<GridCol lg={4.5}>
					<SyllabusPicker
						title="Syllabus *"
						rootElements={makeSyllabusesFromPrintSyllabus(
							allSyllabuses,
							disabledLearningAreas,
						)}
						selected={selectedSyllabuses}
						pending={pendingSyllabuses}
						onChange={handleSyllabus}
						maxSyllabuses={maxSyllabusesFromConfig}
						maxSyllabusesErrorMessage={
							maxSyllabusesMessageFromConfig
						}
						maxVisibleItems={10}
					/>
				</GridCol>
				<GridCol lg={3.5}>
					<TreePicker
						title="Stages *"
						rootElements={makeNestedStageGroups(
							allStages,
							disabledStages,
							customLifeSkillsLabel,
							hideLifeSkillsDivider,
							selectedSyllabuses,
							allSyllabuses,
						)}
						selected={stages}
						onChange={handleStages}
						css={{
							'.tree-picker-id-life_skills': {
								borderTop: 'none',
								marginTop: 0,
								paddingTop: 0,
							},
						}}
						defaultExpanded="all"
						hideExpandToggle={true}
						hideAllDividers={true}
					/>
				</GridCol>
				<GridCol lg={4}>
					<TreePicker
						title="Syllabus elements *"
						rootElements={processedSyllabusElements}
						selected={elements}
						onChange={handleElements}
						css={{
							[`.tree-picker-id-${TREE_ID_LABELS.TAGS.id}`]: {
								borderTop: 'none',
								marginTop: 0,
								paddingTop: 0,
								marginLeft: '2rem',
							},
							[`.tree-picker-id-${TREE_ID_LABELS.ACCESS_POINTS.id}`]:
								{
									marginBottom: '0.5rem',
									paddingLeft: '2rem',
									position: 'relative',
								},
							[`.tree-picker-id-${TREE_ID_LABELS.ACCESS_POINTS.id}::after`]:
								{
									content: '""',
									position: 'absolute',
									bottom: '-0.5rem',
									left: '0',
									right: '0',
									borderBottom:
										'1px solid var(--nsw-grey-01)',
								},
							[`.tree-picker-id-${TREE_ID_LABELS.EXAMPLES.id}`]: {
								marginTop: '1rem',
								marginLeft: '2rem',
							},
						}}
						defaultExpanded="all"
						hideExpandToggle={true}
						hideAllDividers={true}
					/>
				</GridCol>
			</GridWrapper>
		</div>
	)
})

export default NewCustomSyllabusPicker
